import React, {
  useCallback,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";

import {
  createStripeCheckoutSession,
  checkPaymentStatus,
} from "../../utils/api";
import { useStepNavigation } from "../../hooks/useStepNavigation";
import { ClientType } from "../../types/form";

// Make sure to call `loadStripe` outside of a component's render to avoid
// recreating the `Stripe` object on every render.
// This is a public sample test API key.
// Don't submit any personally identifiable information in requests made with this key.
// Sign in to see your own test API key embedded in code samples.
const stripeKey = process.env.REACT_APP_STRIPE_PUBLIC_KEY || "";

const stripePromise = loadStripe(stripeKey);

interface CheckoutFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export const CheckoutForm: React.FC<CheckoutFormProps> = ({
  onSuccess,
  onCancel,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const checkoutInitializedRef = useRef(false);

  // Use environment variable with fallback
  const MONTHLY_PRICE = process.env.REACT_APP_MONTHLY_PRICE_ID || "";
  const PRODUCT_ID = process.env.REACT_APP_PRODUCT_ID || "";

  // Check if Stripe is loaded
  useEffect(() => {
    stripePromise
      .then((stripe) => {
        if (stripe) {
          setStripeLoaded(true);
        }
      })
      .catch((err) => {
        console.error("Stripe loading error:", err);
      });
  }, []);

  const fetchClientSecret = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const clientSecret = await createStripeCheckoutSession(
        MONTHLY_PRICE,
        PRODUCT_ID,
        "usd",
      );

      return clientSecret;
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to create checkout session";
      setError(errorMessage);
      console.error("Checkout error:", err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [MONTHLY_PRICE, PRODUCT_ID]);

  // Initialize client secret when component mounts
  useEffect(() => {
    if (stripeLoaded && !clientSecret && !checkoutInitializedRef.current) {
      checkoutInitializedRef.current = true;
      fetchClientSecret()
        .then(setClientSecret)
        .catch(() => {
          checkoutInitializedRef.current = false;
        });
    }
  }, [stripeLoaded, clientSecret, fetchClientSecret]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      checkoutInitializedRef.current = false;
    };
  }, []);

  const options = useMemo(
    () => ({
      clientSecret,
      onComplete: () => {
        console.log("Payment completed successfully");
        onSuccess?.();
      },
    }),
    [clientSecret, onSuccess],
  );

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="text-red-600 mb-4">Payment Error</div>
        <div className="text-gray-600 mb-4">{error}</div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setError(null);
              checkoutInitializedRef.current = false;
              setClientSecret(null);
            }}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Try Again
          </button>
          {onCancel && (
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
            >
              Cancel
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isLoading || !clientSecret) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Loading payment form...</div>
      </div>
    );
  }

  // Check if Stripe key is available
  if (!stripeKey) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="text-red-600 mb-4">Stripe Configuration Error</div>
        <div className="text-gray-600 mb-4">
          Stripe public key is not configured. Please set
          REACT_APP_STRIPE_PUBLIC_KEY in your environment variables.
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
          <strong>For testing:</strong> Add this to your .env file:
          <br />
          REACT_APP_STRIPE_PUBLIC_KEY=pk_test_your_stripe_key_here
        </div>
      </div>
    );
  }

  return (
    <div id="checkout" className="w-full max-w-md mx-auto">
      <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};

export const Return: React.FC<{ resetFormData: () => void }> = ({
  resetFormData,
}) => {
  const [status, setStatus] = useState<string | null>(null);
  const [customerEmail, setCustomerEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includePaymentStep] = useState(true);
  const [clientType] = useState<ClientType>("Default");
  const stepNavigation = useStepNavigation(includePaymentStep, clientType);

  useEffect(() => {
    const checkPayment = async () => {
      try {
        const queryString = window.location.search;
        const urlParams = new URLSearchParams(queryString);
        const sessionId = urlParams.get("session_id");

        if (!sessionId) {
          setError("No session ID found in URL");
          setIsLoading(false);
          return;
        }

        const data = await checkPaymentStatus(sessionId);

        console.log("Stripe data", data);

        setStatus(data.payment_status.status);
        setCustomerEmail(data.payment_status.customer_email || "");

        // If payment is complete, generate app link
        if (data.payment_status.status === "complete") {
          localStorage.setItem("paymentStatus", "dXNlciBqdXN0IHBhaWQ=");
          // Navigate to template selection (step 2) after payment completion
          stepNavigation.goToStep(2);
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to check payment status";
        setError(errorMessage);
        console.error("Payment status error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    checkPayment();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Checking payment status...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="text-red-600 mb-4">Error</div>
        <div className="text-gray-600 mb-4">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  // if (status === "open") {
  //   return <Navigate to="/checkout" />;
  // }

  if (status === "complete") {
    // Store customer email in localStorage for the success step
    localStorage.setItem("customerEmail", customerEmail);

    // Redirect to the main app with payment completion parameters
    // Use the root path to avoid infinite redirects
    const baseUrl = window.location.origin;
    const existingParams = new URLSearchParams(window.location.search);

    // Add or update payment-related parameters
    // existingParams.set("showPayment", "true");
    existingParams.set("paymentComplete", "true");

    // Redirect to the main app (root path) with payment completion parameters
    // Do not include sessionId in the data sent to server
    existingParams.delete("session_id");
    window.location.href = `${baseUrl}/?${existingParams.toString()}`;
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-center p-8">
      <div className="text-gray-600">Unknown payment status: {status}</div>
    </div>
  );
};
